import { TsDOM as q } from '@/scripts/core/TsDOM'
import { UcoastEl } from '@/scripts/core/UcoastEl'
import { resolveStoqVariantState } from '@/scripts/product/stoq-selling-plan'
import { embroideryInputOutline } from '@/scripts/product/embroidery-input-contrast'
import {
	renderEmbroideryGlyph,
	renderEmbroideryText,
	updateEmbroideryPreview,
} from '@/scripts/product/embroidery-preview'
import {
	embroideryGlyphUrl,
	normalizeEmbroideryCharacter,
	supportsEmbroideryCharacter,
} from '@/scripts/product/embroidery-font'
import {
	addItemsToCart,
	configPostRequest,
	getCart,
	getDOMCartSectionApiIds,
	getPersonalizationCartComponents,
	renderResponseToCartDrawer,
} from '@/scripts/core/cart-functions'
import {
	PUB_SUB_EVENTS,
	getUrlWithPreviewThemeId,
	openBackInStockModal,
	publish,
} from '@/scripts/core/global'

type Stage = 'pair' | 'right' | 'left' | 'review'
type Shoe = 'right' | 'left'
const previewCharacterHeight = 448
type Scope = 'none' | 'right' | 'both'

type Slot = {
	kind: 'character' | 'icon'
	value: string
	color: string
	originalColors?: boolean
	iconUrl?: string
}

type ShoeDesign = {
	font: 'bubble' | 'script' | 'block'
	slots: [Slot | null, Slot | null, Slot | null]
}

type PersonalizationState = {
	version: 2
	right: ShoeDesign
	left: ShoeDesign | null
}

type PersonalizationTransfer = {
	state: PersonalizationState
	scope?: Scope
	drafts?: Partial<Record<Shoe, ShoeDesign>>
	sizeLabel: string
	gender: string
}

type CartLine = {
	id: number
	variant_id: number
	product_id: number
	key: string
	quantity: number
	properties: Record<string, string>
}

type IconDefinition = {
	handle: string
	url: string
	useOriginalColors: boolean
}

const emptyShoe = (): ShoeDesign => ({
	font: 'bubble',
	slots: [null, null, null],
})

export class ProductPersonalizationPage extends UcoastEl {
	static htmlSelector = 'product-personalization-page'

	private state: PersonalizationState = {
		version: 2,
		right: emptyShoe(),
		left: null,
	}
	private stage: Stage = 'pair'
	private scope: Scope = 'right'
	private drafts: Partial<Record<Shoe, ShoeDesign>> = {}
	private pendingRemoval: Scope = 'right'
	private activeShoe: Shoe = 'right'
	private lastDesignStage: Shoe = 'right'
	private activeSlot: Record<Shoe, number> = { right: 0, left: 0 }
	private pendingColors: Record<Shoe, string> = {
		right: '',
		left: '',
	}
	private editLine = ''
	private editParent?: CartLine
	private editChild?: CartLine
	private submitting = false
	private replaceOnInsert = false
	private addedSignature = ''
	private addedBeat = false
	private addedBeatTimer?: ReturnType<typeof setTimeout>
	private changingProduct = false
	private stageHistoryBound = false
	private stageHistoryDepth = 0
	private popStateListener?: (event: PopStateEvent) => void
	private iconInfoPointerState = new WeakMap<HTMLButtonElement, boolean>()
	private readonly onIconInfoDocumentPointerDown = (event: PointerEvent) => {
		const target = event.target
		if (
			target instanceof Element &&
			this.contains(target) &&
			target.closest('.personalization-page__icon-info')
		)
			return
		this.closeIconInfoPopovers()
	}
	private readonly onIconInfoPointerDown = (event: PointerEvent) => {
		const button = (event.target as HTMLElement).closest<HTMLButtonElement>(
			'[data-uc-personalization-icon-info]'
		)
		if (!button) return
		this.iconInfoPointerState.set(
			button,
			button.getAttribute('aria-expanded') === 'true'
		)
	}
	private readonly onIconInfoClick = (event: MouseEvent) => {
		const button = (event.target as HTMLElement).closest<HTMLButtonElement>(
			'[data-uc-personalization-icon-info]'
		)
		if (!button) return
		event.preventDefault()
		event.stopPropagation()
		const pointerState = this.iconInfoPointerState.get(button)
		const isOpen = button.getAttribute('aria-expanded') === 'true'
		this.setIconInfoOpen(
			button,
			pointerState === undefined ? !isOpen : !pointerState
		)
		this.iconInfoPointerState.delete(button)
	}
	private readonly onIconInfoKeyDown = (event: KeyboardEvent) => {
		const button = (event.target as HTMLElement).closest<HTMLButtonElement>(
			'[data-uc-personalization-icon-info]'
		)
		if (!button) return
		event.stopPropagation()
		if (event.key === 'Escape') {
			event.preventDefault()
			this.setIconInfoOpen(button, false)
			return
		}
		if (!['Enter', ' '].includes(event.key)) return
		event.preventDefault()
		this.setIconInfoOpen(
			button,
			button.getAttribute('aria-expanded') !== 'true'
		)
	}

	override connectedCallback() {
		super.connectedCallback()
		if (!this.querySelector('[data-uc-personalization-stage]')) return
		const form = q.rs<HTMLFormElement>(
			'[data-uc-personalization-form]',
			this
		)
		form.inert = true

		this.initializeColors()
		this.editLine =
			new URLSearchParams(window.location.search).get('edit') ?? ''
		const transferred = this.restoreTransferredState()
		if (!this.editLine && !transferred) this.restoreDraft()
		this.applyUrlStage()
		this.syncVariantAvailability()
		this.updateBackLink()
		this.addEventListener('click', this.onClick.bind(this))
		this.addEventListener('change', this.onChange.bind(this))
		this.addEventListener('input', this.onInput.bind(this))
		this.addEventListener('beforeinput', this.onBeforeInput.bind(this))
		this.addEventListener('focusin', this.onFocusIn.bind(this))
		this.addEventListener('focusout', this.onFocusOut.bind(this))
		this.addEventListener('keydown', this.onKeyDown.bind(this))
		this.addEventListener('pointerdown', this.onIconInfoPointerDown, true)
		this.addEventListener('click', this.onIconInfoClick, true)
		this.addEventListener('keydown', this.onIconInfoKeyDown, true)
		document.addEventListener(
			'pointerdown',
			this.onIconInfoDocumentPointerDown
		)
		form.addEventListener('submit', this.onSubmit.bind(this))
		const editReady = this.editLine
			? this.initializeEditMode()
			: Promise.resolve()
		this.render()
		this.bindStageHistory()
		if (transferred) this.finishIntro()
		else void this.startIntro(editReady)
	}

	override disconnectedCallback() {
		super.disconnectedCallback()
		document.removeEventListener(
			'pointerdown',
			this.onIconInfoDocumentPointerDown
		)
		this.unbindStageHistory()
		this.finishIntro()
		this.drafts = {}
		if (this.addedBeatTimer) clearTimeout(this.addedBeatTimer)
	}

	private async startIntro(editReady: Promise<void>) {
		const photo = q.os<HTMLImageElement>(
			'.personalization-page__preview-image',
			this
		)
		try {
			await Promise.all([
				editReady,
				document.fonts.ready,
				photo?.decode(),
			])
			if (!this.isConnected) return
			this.finishIntro()
		} catch {
			if (!this.isConnected) return
			clearTimeout(Number(this.dataset.ucIntroWatchdog))
			this.dataset.ucIntroState = 'error'
			q.os<HTMLElement>(
				'[data-uc-personalization-intro]',
				this
			)?.setAttribute('role', 'alert')
		}
	}

	private finishIntro() {
		if (this.dataset.ucIntroState !== 'loading') return
		clearTimeout(Number(this.dataset.ucIntroWatchdog))
		this.dataset.ucIntroState = 'ready'
		const intro = q.os<HTMLElement>('[data-uc-personalization-intro]', this)
		const form = q.os<HTMLFormElement>(
			'[data-uc-personalization-form]',
			this
		)
		intro?.setAttribute('aria-hidden', 'true')
		if (form) form.inert = false
	}

	private initializeColors() {
		const color = this.dataset.ucPersonalizationDefaultColor || '#17396f'
		this.pendingColors = {
			right: color,
			left: color,
		}
	}

	private get transferKey() {
		return 'brunch-personalization-transfer'
	}

	private get draftKey() {
		return `brunch-personalization-draft:${this.dataset.ucProductHandle ?? window.location.pathname}`
	}

	private hasValidStateSlots(state: PersonalizationState) {
		return (
			state.right?.slots?.length === 3 &&
			(!state.left || state.left.slots?.length === 3)
		)
	}

	private hasValidDraftSlots(drafts?: Partial<Record<Shoe, ShoeDesign>>) {
		return (
			(!drafts?.right || drafts.right.slots?.length === 3) &&
			(!drafts?.left || drafts.left.slots?.length === 3)
		)
	}

	private restoreDraft() {
		const raw = window.sessionStorage.getItem(this.draftKey)
		if (!raw) return
		try {
			const draft = JSON.parse(raw) as {
				state: PersonalizationState
				scope?: Scope
				stage?: Stage
				variantId?: string
				drafts?: Partial<Record<Shoe, ShoeDesign>>
				lastDesignStage?: Shoe
			}
			if (
				!this.hasValidStateSlots(draft.state) ||
				!this.hasValidDraftSlots(draft.drafts)
			)
				throw new TypeError('Invalid personalization draft')
			this.hydrateState(draft.state)
			this.scope =
				draft.scope === 'both' || draft.scope === 'none'
					? draft.scope
					: 'right'
			if (this.scope === 'right') this.state.left = null
			this.drafts = draft.drafts ?? {}
			if (
				draft.lastDesignStage === 'left' ||
				draft.lastDesignStage === 'right'
			)
				this.lastDesignStage = draft.lastDesignStage
			const variant = this.querySelector<HTMLInputElement>(
				`[data-uc-personalization-variant][value="${draft.variantId}"]:not(:disabled)`
			)
			if (variant) {
				variant.checked = true
				this.dataset.ucParentVariantId = variant.value
				this.dataset.ucParentPrice =
					variant.dataset.ucPersonalizationPrice ?? '0'
				this.syncVariantAvailability()
			}
			this.stage =
				draft.stage === 'right' ||
				draft.stage === 'left' ||
				draft.stage === 'review'
					? draft.stage
					: 'pair'
			if (this.stage === 'left' && this.scope !== 'both')
				this.stage = 'right'
			if (
				this.stage === 'review' &&
				!this.isComplete &&
				this.scope !== 'none'
			)
				this.stage = 'right'
			if (this.stage === 'right' || this.stage === 'left')
				this.activeShoe = this.stage
		} catch {
			this.clearDraft()
		}
	}

	private persistDraft() {
		if (this.editLine) return
		window.sessionStorage.setItem(
			this.draftKey,
			JSON.stringify({
				state: this.state,
				scope: this.scope,
				stage: this.stage,
				variantId: this.selectedVariant?.value ?? '',
				drafts: this.drafts,
				lastDesignStage: this.lastDesignStage,
			})
		)
	}

	private clearDraft() {
		window.sessionStorage.removeItem(this.draftKey)
	}

	private restoreTransferredState() {
		const raw = window.sessionStorage.getItem(this.transferKey)
		if (!raw) return false
		window.sessionStorage.removeItem(this.transferKey)
		try {
			const transfer = JSON.parse(raw) as
				| PersonalizationState
				| PersonalizationTransfer
			const state = 'state' in transfer ? transfer.state : transfer
			if (
				!this.hasValidStateSlots(state) ||
				('state' in transfer &&
					!this.hasValidDraftSlots(transfer.drafts))
			)
				return false
			this.hydrateState(state)
			this.stage = 'pair'
			this.scope = this.state.left ? 'both' : 'right'
			if ('state' in transfer) {
				this.drafts = transfer.drafts ?? {}
				if (transfer.scope === 'none') this.scope = 'none'
				this.restorePairSelection(transfer.sizeLabel, transfer.gender)
			}
			return true
		} catch {
			return false
		}
	}

	private getVariantSizeLabel(input: HTMLInputElement, gender: string) {
		const label = Array.from(
			this.querySelectorAll<HTMLLabelElement>('label[for]')
		).find((candidate) => candidate.htmlFor === input.id)
		if (!label) return ''
		const genderLabel = q.os<HTMLElement>(
			`[data-uc-personalization-gender="${gender}"]`,
			label
		)
		return (
			genderLabel?.textContent?.trim() || label.textContent?.trim() || ''
		)
	}

	private restorePairSelection(sizeLabel: string, gender: string) {
		for (const input of this.querySelectorAll<HTMLInputElement>(
			'[name="personalization-page-size-gender"]'
		))
			input.checked = input.value === gender

		if (!sizeLabel) return
		const variants = Array.from(
			this.querySelectorAll<HTMLInputElement>(
				'[data-uc-personalization-variant]'
			)
		)
		const matchingVariant = variants.find(
			(input) => this.getVariantSizeLabel(input, gender) === sizeLabel
		)
		for (const input of variants) input.checked = input === matchingVariant
		if (!matchingVariant) {
			this.dataset.ucParentVariantId = ''
			this.dataset.ucParentPrice = '0'
			return
		}
		this.updateVariantUrl(matchingVariant)
	}

	private getShoeColor(shoe: Shoe, design = this.getShoe(shoe)) {
		return (
			design.slots.find((slot) => slot && !slot.originalColors)?.color ||
			this.pendingColors[shoe] ||
			this.dataset.ucPersonalizationDefaultColor ||
			'#17396f'
		)
	}

	private normalizeShoeColors(shoe: Shoe, design = this.getShoe(shoe)) {
		const color = this.getShoeColor(shoe, design)
		this.pendingColors[shoe] = color
		for (const slot of design.slots) {
			if (slot && !slot.originalColors) slot.color = color
		}
	}

	private getColorName(color: string) {
		return (
			Array.from(
				this.querySelectorAll<HTMLButtonElement>(
					'[data-uc-personalization-thread-color]'
				)
			).find(
				(button) =>
					button.dataset.ucPersonalizationColorHex?.toLowerCase() ===
					color.toLowerCase()
			)?.dataset.ucPersonalizationColorName ?? ''
		)
	}

	private onFocusIn(event: FocusEvent) {
		const infoButton = (
			event.target as HTMLElement
		).closest<HTMLButtonElement>('[data-uc-personalization-icon-info]')
		if (infoButton) {
			this.setIconInfoOpen(infoButton, true)
			return
		}
		const input = (event.target as HTMLElement).closest<HTMLInputElement>(
			'[data-uc-personalization-slot-input]'
		)
		if (!input) return
		this.replaceOnInsert = true
		this.setActiveSlot(input)
		this.renderComposer(this.getInputShoe(input))
		input.select()
	}

	private onFocusOut(event: FocusEvent) {
		const infoButton = (
			event.target as HTMLElement
		).closest<HTMLButtonElement>('[data-uc-personalization-icon-info]')
		if (!infoButton) return
		const wrapper = infoButton.closest('.personalization-page__icon-info')
		if (
			event.relatedTarget instanceof Node &&
			wrapper?.contains(event.relatedTarget)
		)
			return
		this.setIconInfoOpen(infoButton, false)
	}

	private setIconInfoOpen(button: HTMLButtonElement, open: boolean) {
		if (open) this.closeIconInfoPopovers(button)
		button.setAttribute('aria-expanded', String(open))
		button
			.closest('.personalization-page__icon-info')
			?.classList.toggle('is-open', open)
		button
			.closest('.personalization-page__icon-accordion-summary')
			?.classList.toggle('is-info-open', open)
		const tipId = button.getAttribute('aria-controls')
		if (!tipId) return
		this.querySelector(`#${CSS.escape(tipId)}`)?.setAttribute(
			'aria-hidden',
			String(!open)
		)
	}

	private closeIconInfoPopovers(except?: HTMLButtonElement) {
		for (const button of this.querySelectorAll<HTMLButtonElement>(
			'[data-uc-personalization-icon-info][aria-expanded="true"]'
		)) {
			if (button !== except) this.setIconInfoOpen(button, false)
		}
	}

	private onKeyDown(event: KeyboardEvent) {
		if (event.isComposing) return
		if (!['Backspace', 'Delete'].includes(event.key)) return
		const input = (event.target as HTMLElement).closest<HTMLInputElement>(
			'[data-uc-personalization-slot-input]'
		)
		if (!input) return

		const shoe = this.getInputShoe(input)
		const index = this.getInputIndex(input)
		const design = this.getShoe(shoe)
		if (design.slots[index]) {
			event.preventDefault()
			design.slots[index] = null
			this.render()
			input.focus({ preventScroll: true })
			return
		}
		if (event.key !== 'Backspace' || index === 0) return
		event.preventDefault()
		this.activeSlot[shoe] = index - 1
		design.slots[index - 1] = null
		this.render()
		this.getSlotInput(shoe, index - 1)?.focus()
	}

	private onBeforeInput(event: InputEvent) {
		if (event.isComposing || !event.inputType.startsWith('insert')) return
		const input = q
			.ot(event)
			?.closest<HTMLInputElement>('[data-uc-personalization-slot-input]')
		if (!input?.value) return
		if (!this.replaceOnInsert) {
			event.preventDefault()
			return
		}
		const replacement =
			event.data ?? event.dataTransfer?.getData('text/plain')
		if (event.cancelable && replacement != null) {
			event.preventDefault()
			input.value = replacement
			this.onInput(event)
			return
		}
		input.select()
	}

	private onInput(event: Event) {
		if ((event as InputEvent).isComposing) return
		const input = (event.target as HTMLElement).closest<HTMLInputElement>(
			'[data-uc-personalization-slot-input]'
		)
		if (!input) return

		const shoe = this.getInputShoe(input)
		const index = this.getInputIndex(input)
		this.activeShoe = shoe
		this.activeSlot[shoe] = index
		const font = this.getShoe(shoe).font
		const chars = Array.from(input.value)
			.map((character) => normalizeEmbroideryCharacter(font, character))
			.filter(Boolean)
		this.replaceOnInsert = false
		if (chars.length > 1) {
			this.applyCharacters(shoe, index, chars)
			this.render()
			this.focusNextEmptySlot(shoe, index)
			return
		}
		const value = chars.at(-1) ?? ''
		input.value = value
		this.getShoe(shoe).slots[index] = value
			? {
					kind: 'character',
					value,
					color: this.getShoeColor(shoe),
					originalColors: false,
				}
			: null
		this.render()
		if (value && supportsEmbroideryCharacter(font, value))
			this.focusNextEmptySlot(shoe, index)
	}

	private applyCharacters(shoe: Shoe, startIndex: number, chars: string[]) {
		const design = this.getShoe(shoe)
		const color = this.getShoeColor(shoe)
		for (const [offset, value] of chars
			.slice(0, design.slots.length - startIndex)
			.entries()) {
			design.slots[startIndex + offset] = {
				kind: 'character',
				value,
				color,
				originalColors: false,
			}
			this.activeSlot[shoe] = startIndex + offset
		}
	}

	private onChange(event: Event) {
		const input = event.target as HTMLInputElement
		if (input.matches('[data-uc-personalization-font]')) {
			const shoe = this.getInputShoe(input)
			this.getShoe(shoe).font = input.value as ShoeDesign['font']
			for (const slot of this.getShoe(shoe).slots) {
				if (slot?.kind === 'character')
					slot.value = normalizeEmbroideryCharacter(
						this.getShoe(shoe).font,
						slot.value
					)
			}
			this.render()
			return
		}
		if (input.matches('[data-uc-personalization-scope]')) {
			this.changeScope(input)
			return
		}
		if (input.matches('[data-uc-personalization-variant]')) {
			this.updateVariantUrl(input)
			this.render()
			return
		}
		if (input.matches('[data-uc-personalization-final-sale]')) {
			this.render()
			return
		}
		if (input.name === 'personalization-page-size-gender') this.render()
	}

	private onClick(event: MouseEvent) {
		if (this.submitting) {
			event.preventDefault()
			return
		}
		const target = event.target as HTMLElement
		if (target.closest('[data-uc-personalization-back-link]')) {
			if (this.stage !== 'pair') {
				event.preventDefault()
				this.goBack()
			}
			return
		}
		const inspectFoot = target.closest<HTMLElement>(
			'[data-uc-personalization-inspect-foot]'
		)
		if (inspectFoot) {
			event.preventDefault()
			if (this.stage === 'right' || this.stage === 'left') {
				const shoe = inspectFoot.dataset
					.ucPersonalizationInspectFoot as Shoe
				if (shoe === 'right' || (shoe === 'left' && this.state.left))
					this.showStage(shoe, 'push', true)
				return
			}
			this.activeShoe =
				inspectFoot.dataset.ucPersonalizationInspectFoot === 'left' &&
				this.state.left
					? 'left'
					: 'right'
			this.renderPreview()
			return
		}
		const shoeTarget = target.closest<HTMLElement>(
			'[data-uc-personalization-shoe-target]'
		)
		if (shoeTarget) {
			event.preventDefault()
			const shoe = shoeTarget.dataset.ucPersonalizationShoeTarget as Shoe
			if (shoe === 'right' || (shoe === 'left' && this.state.left))
				this.showStage(shoe)
			return
		}
		const slotInput = target.closest<HTMLInputElement>(
			'[data-uc-personalization-slot-input]'
		)
		if (slotInput) {
			slotInput.select()
			return
		}
		const product = target.closest<HTMLButtonElement>(
			'button[data-uc-personalization-product-url]'
		)
		if (product) {
			event.preventDefault()
			void this.changeProduct(
				product.dataset.ucPersonalizationProductUrl ?? ''
			)
			return
		}

		const icon = target.closest<HTMLButtonElement>(
			'[data-uc-personalization-icon]'
		)
		if (icon) {
			event.preventDefault()
			this.selectIcon(icon)
			return
		}

		const color = target.closest<HTMLButtonElement>(
			'[data-uc-personalization-thread-color]'
		)
		if (color) {
			event.preventDefault()
			this.selectColor(color)
			return
		}

		const stage = target.closest<HTMLButtonElement>(
			'[data-uc-personalization-stage-target]'
		)
		if (stage) {
			event.preventDefault()
			this.requestStage(
				stage.dataset.ucPersonalizationStageTarget as Stage
			)
			return
		}

		const next = target.closest<HTMLButtonElement>(
			'[data-uc-personalization-next]'
		)
		if (next) {
			event.preventDefault()
			if (this.needsWaitlist) {
				this.openWaitlist(next)
				return
			}
			this.requestStage(next.dataset.ucPersonalizationNext as Stage)
			return
		}

		const back = target.closest<HTMLButtonElement>(
			'[data-uc-personalization-back]'
		)
		if (back) {
			event.preventDefault()
			this.showStage(back.dataset.ucPersonalizationBack as Stage)
			return
		}

		if (target.closest('[data-uc-personalization-right-continue]')) {
			event.preventDefault()
			if (this.scope === 'both' && !this.state.left) {
				this.state.left = structuredClone(
					this.drafts.left ?? this.state.right
				)
				delete this.drafts.left
				this.normalizeShoeColors('left', this.state.left)
			}
			this.showStage(this.scope === 'both' ? 'left' : 'review')
			return
		}

		if (target.closest('[data-uc-personalization-left-continue]')) {
			event.preventDefault()
			this.showStage('review')
			return
		}

		if (target.closest('[data-uc-personalization-review-upsell-button]')) {
			event.preventDefault()
			if (this.scope === 'none') {
				this.setScope('right')
				this.showStage('right')
				return
			}
			this.scope = 'both'
			if (!this.state.left) {
				this.state.left = structuredClone(
					this.drafts.left ?? this.state.right
				)
				delete this.drafts.left
				this.normalizeShoeColors('left', this.state.left)
			}
			this.showStage('left')
			return
		}

		if (target.closest('[data-uc-personalization-remove-left]')) {
			event.preventDefault()
			this.setScope('right')
			return
		}
		if (target.closest('[data-uc-personalization-remove-right]')) {
			event.preventDefault()
			if (this.scope === 'right') this.setScope('none')
			return
		}
		if (target.closest('[data-uc-personalization-confirm-remove]')) {
			event.preventDefault()
			q.rs<HTMLDialogElement>(
				'[data-uc-personalization-remove-dialog]',
				this
			).close()
			this.setScope(this.pendingRemoval, true)
			if (this.stage === 'review')
				q.os<HTMLButtonElement>(
					'[data-uc-personalization-review-upsell-button]',
					this
				)?.focus()
			return
		}
		if (target.closest('[data-uc-personalization-cancel-remove]')) {
			event.preventDefault()
			q.rs<HTMLDialogElement>(
				'[data-uc-personalization-remove-dialog]',
				this
			).close()
			return
		}

		if (target.closest('[data-uc-personalization-preview-link]')) {
			event.preventDefault()
			q.os<HTMLElement>(
				'.personalization-page__media-column',
				this
			)?.scrollIntoView({
				block: 'start',
				behavior: window.matchMedia('(prefers-reduced-motion: reduce)')
					.matches
					? 'auto'
					: 'smooth',
			})
			return
		}

		if (target.closest('[data-uc-personalization-review-back]')) {
			event.preventDefault()
			this.goBack()
			return
		}
	}

	private changeScope(input: HTMLInputElement) {
		const nextScope: Scope = input.value === 'both' ? 'both' : 'right'
		this.setScope(nextScope)
	}

	private setScope(nextScope: Scope, confirmed = false) {
		if (nextScope === 'none' && this.scope === 'both') return false
		const removedDesign =
			nextScope === 'none' ? this.state.right : this.state.left
		if (
			nextScope !== 'both' &&
			removedDesign &&
			this.hasDesign(removedDesign) &&
			!confirmed
		) {
			this.pendingRemoval = nextScope
			for (const input of this.querySelectorAll<HTMLInputElement>(
				'[data-uc-personalization-scope]'
			))
				input.checked = input.value === this.scope
			const message = q.os<HTMLElement>(
				'[data-uc-personalization-remove-message]',
				this
			)
			if (message)
				message.textContent =
					nextScope === 'none'
						? (this.dataset.ucPersonalizationRemoveRightConfirm ??
							'')
						: (this.dataset.ucPersonalizationRemoveLeftConfirm ??
							'')
			q.rs<HTMLDialogElement>(
				'[data-uc-personalization-remove-dialog]',
				this
			).showModal()
			return false
		}
		if (this.scope === 'none' && nextScope !== 'none') {
			this.state.right = structuredClone(this.drafts.right ?? emptyShoe())
			delete this.drafts.right
			this.normalizeShoeColors('right', this.state.right)
		}
		this.scope = nextScope
		if (nextScope !== 'both') {
			if (this.state.left && this.hasDesign(this.state.left))
				this.drafts.left = structuredClone(this.state.left)
			this.state.left = null
			this.activeShoe = 'right'
			this.lastDesignStage = 'right'
		}
		if (nextScope === 'none') {
			this.drafts.right = structuredClone(this.state.right)
			this.state.right = emptyShoe()
		}
		this.render()
		return true
	}

	private selectIcon(button: HTMLButtonElement) {
		const shoe = this.getInputShoe(button)
		const index = this.activeSlot[shoe]
		const originalColors =
			button.dataset.ucPersonalizationIconOriginalColors === 'true'
		this.getShoe(shoe).slots[index] = {
			kind: 'icon',
			value: button.dataset.ucPersonalizationIconHandle ?? '',
			color: this.getShoeColor(shoe),
			originalColors,
			iconUrl: button.dataset.ucPersonalizationIconUrl ?? '',
		}
		this.render()
		this.focusNextEmptySlot(shoe, index)
	}

	private selectColor(button: HTMLButtonElement) {
		const shoe = this.getInputShoe(button)
		const color = button.dataset.ucPersonalizationColorHex ?? ''
		if (!color) return
		this.pendingColors[shoe] = color
		for (const slot of this.getShoe(shoe).slots) {
			if (slot && !slot.originalColors) slot.color = color
		}
		this.render()
	}

	private requestStage(stage: Stage) {
		if (stage === 'pair') {
			this.showStage(stage)
			return
		}
		if (!this.selectedVariant) return
		if (stage === 'right') {
			if (this.scope === 'none') this.setScope('right')
			const designStage =
				this.scope === 'both' &&
				this.lastDesignStage === 'left' &&
				(this.stage === 'left' || this.stage === 'review')
					? 'left'
					: 'right'
			this.showStage(designStage)
			return
		}
		if (
			stage === 'left' &&
			this.scope === 'both' &&
			this.hasSupportedDesign(this.state.right)
		) {
			this.showStage(stage)
			return
		}
		if (stage === 'review' && this.isComplete) this.showStage(stage)
	}

	private showStage(
		stage: Stage,
		historyMode: 'push' | 'replace' | 'none' = 'push',
		previewOnMobile = false
	) {
		const previous = this.stage
		const previewFirst =
			window.matchMedia('(max-width: 749px)').matches &&
			(previewOnMobile || (previous === 'pair' && stage === 'right'))
		this.stage = stage
		if (stage === 'right' || stage === 'left') {
			this.lastDesignStage = stage
			this.activeShoe = stage
		} else if (stage === 'pair') this.activeShoe = 'right'
		this.render()
		q.os<HTMLElement>(
			`[data-uc-personalization-stage="${stage}"]`,
			this
		)?.focus({ preventScroll: true })
		q.os<HTMLElement>(
			previewFirst
				? '.personalization-page__media-column'
				: '.personalization-page__editor-column',
			this
		)?.scrollIntoView({
			block: 'start',
		})
		if (historyMode === 'none') return
		if (historyMode === 'push' && previous === stage) return
		this.writeStageHistory(historyMode)
	}

	private goBack() {
		if (this.stageHistoryBound && this.stageHistoryDepth > 0) {
			window.history.back()
			return
		}
		if (this.stage === 'review') {
			this.showStage(
				this.scope === 'none'
					? 'pair'
					: this.scope === 'both'
						? this.lastDesignStage
						: 'right'
			)
			return
		}
		this.showStage(this.stage === 'left' ? 'right' : 'pair')
	}

	private isStage(value: string | null | undefined): value is Stage {
		return (
			value === 'pair' ||
			value === 'right' ||
			value === 'left' ||
			value === 'review'
		)
	}

	private clampStage(stage: Stage) {
		if (stage === 'left' && this.scope !== 'both') return 'right'
		if (stage === 'review' && !this.isComplete && this.scope !== 'none')
			return 'right'
		return stage
	}

	private applyUrlStage() {
		const step = new URLSearchParams(window.location.search).get('step')
		if (!this.isStage(step)) return
		this.stage = this.clampStage(step)
		if (this.stage === 'right' || this.stage === 'left') {
			this.activeShoe = this.stage
			this.lastDesignStage = this.stage
		}
	}

	private stageHistoryState(
		stage = this.stage,
		depth = this.stageHistoryDepth,
		product = this.dataset.ucProductHandle ?? ''
	) {
		return { brunchPersonalization: { stage, depth, product } }
	}

	private historyPayload(state: unknown) {
		if (!state || typeof state !== 'object') return null
		const payload = (
			state as {
				brunchPersonalization?: {
					stage?: string
					depth?: number
					product?: string
				}
			}
		).brunchPersonalization
		if (!this.isStage(payload?.stage)) return null
		return {
			stage: payload.stage,
			depth: Number.isFinite(payload.depth) ? Number(payload.depth) : 0,
			product: payload.product ?? '',
		}
	}

	private canWriteHistory() {
		return typeof window.history?.pushState === 'function'
	}

	private stageLocation(stage: Stage) {
		const url = getUrlWithPreviewThemeId(
			`${window.location.pathname || '/'}${window.location.search || ''}${
				window.location.hash || ''
			}`
		)
		url.searchParams.set('step', stage)
		return url.toString()
	}

	private writeStageHistory(mode: 'push' | 'replace') {
		if (!this.canWriteHistory()) return
		if (mode === 'push') this.stageHistoryDepth += 1
		const url = this.stageLocation(this.stage)
		const state = this.stageHistoryState()
		if (mode === 'push') window.history.pushState(state, '', url)
		else window.history.replaceState(state, '', url)
	}

	private bindStageHistory() {
		if (this.stageHistoryBound || !this.canWriteHistory()) return
		this.stageHistoryBound = true
		const existing = this.historyPayload(window.history.state)
		const handle = this.dataset.ucProductHandle ?? ''
		if (existing && (!existing.product || existing.product === handle))
			this.stageHistoryDepth = existing.depth
		const listener = this.handlePopState.bind(this)
		this.popStateListener = listener
		window.addEventListener('popstate', listener)
		this.writeStageHistory('replace')
	}

	private unbindStageHistory() {
		const listener = this.popStateListener
		if (!listener) return
		window.removeEventListener('popstate', listener)
		this.popStateListener = undefined
		this.stageHistoryBound = false
		this.stageHistoryDepth = 0
	}

	private handlePopState(event: PopStateEvent) {
		const payload = this.historyPayload(event.state)
		if (!payload) return
		const handle = this.dataset.ucProductHandle ?? ''
		if (payload.product && handle && payload.product !== handle) {
			window.location.reload()
			return
		}
		this.stageHistoryDepth = payload.depth
		const stage = this.clampStage(payload.stage)
		if (stage === this.stage) return
		this.showStage(stage, 'none')
	}

	private updateVariantUrl(input: HTMLInputElement) {
		this.dataset.ucParentVariantId = input.value
		this.dataset.ucParentPrice = input.dataset.ucPersonalizationPrice ?? '0'
		this.syncVariantAvailability()
		const url = getUrlWithPreviewThemeId(this.dataset.ucProductUrl ?? '')
		url.searchParams.set('view', 'personalize')
		url.searchParams.set('variant', input.value)
		this.preserveReturnTo(url)
		url.searchParams.set('step', this.stage)
		window.history.replaceState(
			this.stageHistoryState(),
			'',
			url.toString()
		)
	}

	private syncVariantAvailability() {
		const input = this.selectedVariant
		if (!input || this.editLine) return
		const status = input.dataset.ucPersonalizationInventoryStatus ?? ''
		const resolved = resolveStoqVariantState({
			variantId: input.value,
			available: input.dataset.available === 'true',
			preorder: status === 'PreOrder',
			inventoryStatus: status,
			inventoryStatusOverride:
				input.dataset.ucPersonalizationInventoryOverride,
			inventoryStatusText: '',
			liquidSellingPlanId: input.dataset.ucPersonalizationSellingPlanId,
		})
		this.dataset.ucCurrentInventoryStatus = resolved.inventoryStatus
		this.dataset.ucCurrentSellingPlanId = resolved.sellingPlanId
		this.dataset.ucCurrentShippingText =
			resolved.shippingText ||
			input.dataset.ucPersonalizationShippingText ||
			''
	}

	private getReturnTo() {
		const value = new URLSearchParams(window.location.search).get(
			'return_to'
		)
		if (!value || !value.startsWith('/') || value.startsWith('//'))
			return ''
		const url = new URL(value, window.location.origin)
		if (url.origin !== window.location.origin) return ''
		return `${url.pathname}${url.search}${url.hash}`
	}

	private preserveReturnTo(url: URL) {
		const returnTo = this.getReturnTo()
		if (returnTo) url.searchParams.set('return_to', returnTo)
	}

	private updateBackLink() {
		const returnTo = this.getReturnTo()
		const link = q.os<HTMLAnchorElement>(
			'[data-uc-personalization-back-link]',
			this
		)
		if (!link) return
		const url = getUrlWithPreviewThemeId(
			returnTo || this.dataset.ucProductUrl || ''
		)
		if (!returnTo)
			url.searchParams.set(
				'variant',
				this.selectedVariant?.value ||
					this.dataset.ucParentVariantId ||
					''
			)
		link.href = url.toString()
		const label = q.os<HTMLElement>(
			'[data-uc-personalization-back-label]',
			link
		)
		if (label)
			label.textContent =
				this.stage !== 'pair'
					? (link.dataset.ucPersonalizationStepLabel ?? '')
					: returnTo
						? (link.dataset.ucPersonalizationCollectionLabel ?? '')
						: (link.dataset.ucPersonalizationProductLabel ?? '')
	}

	private setActiveSlot(input: HTMLInputElement) {
		const shoe = this.getInputShoe(input)
		this.activeShoe = shoe
		this.activeSlot[shoe] = this.getInputIndex(input)
	}

	private focusNextEmptySlot(shoe: Shoe, currentIndex: number) {
		const next = this.findNextEmptySlot(shoe, currentIndex)
		if (next === null) return
		this.activeSlot[shoe] = next
		this.renderComposer(shoe)
		this.getSlotInput(shoe, next)?.focus()
	}

	private findNextEmptySlot(shoe: Shoe, currentIndex: number) {
		const design = this.getShoe(shoe)
		for (let offset = 1; offset < design.slots.length; offset += 1) {
			const index = (currentIndex + offset) % design.slots.length
			if (!design.slots[index]) return index
		}
		return null
	}

	private get selectedVariant() {
		return q.os<HTMLInputElement>(
			'[data-uc-personalization-variant]:checked',
			this
		)
	}

	private get iconDefinitions() {
		const definitions = new Map<string, IconDefinition>()
		for (const button of this.querySelectorAll<HTMLButtonElement>(
			'[data-uc-personalization-icon]'
		)) {
			const handle = button.dataset.ucPersonalizationIconHandle
			if (!handle || definitions.has(handle)) continue
			definitions.set(handle, {
				handle,
				url: button.dataset.ucPersonalizationIconUrl ?? '',
				useOriginalColors:
					button.dataset.ucPersonalizationIconOriginalColors ===
					'true',
			})
		}
		return definitions
	}

	private get isComplete() {
		if (this.scope === 'none') return Boolean(this.selectedVariant)
		if (!this.selectedVariant || !this.hasSupportedDesign(this.state.right))
			return false
		return this.scope === 'right'
			? true
			: Boolean(
					this.state.left && this.hasSupportedDesign(this.state.left)
				)
	}

	private isValid() {
		const acknowledgement = q.os<HTMLInputElement>(
			'[data-uc-personalization-final-sale]',
			this
		)
		return (
			!this.submitting &&
			!(this.addedBeat && this.addedCurrent) &&
			this.isComplete &&
			['InStock', 'PreOrder'].includes(
				this.dataset.ucCurrentInventoryStatus ?? ''
			) &&
			(this.scope === 'none' ||
				this.editLine !== '' ||
				acknowledgement?.checked === true)
		)
	}

	private currentAddSignature() {
		return JSON.stringify({
			variant: this.selectedVariant?.value ?? '',
			scope: this.scope,
			state: this.state,
		})
	}

	private get addedCurrent() {
		return (
			this.addedSignature !== '' &&
			this.addedSignature === this.currentAddSignature()
		)
	}

	private markAdded() {
		this.addedSignature = this.currentAddSignature()
		this.addedBeat = true
		if (this.addedBeatTimer) clearTimeout(this.addedBeatTimer)
		this.addedBeatTimer = setTimeout(() => {
			this.addedBeat = false
			this.addedBeatTimer = undefined
			this.render()
		}, 1000)
	}

	private get needsWaitlist() {
		return (
			!this.editLine &&
			['OutOfStock', 'ComingSoon'].includes(
				this.dataset.ucCurrentInventoryStatus ?? ''
			)
		)
	}

	private openWaitlist(opener: HTMLElement) {
		const variant = this.selectedVariant
		if (!variant || this.submitting) return
		const url = getUrlWithPreviewThemeId(this.dataset.ucProductUrl ?? '')
		url.searchParams.set('variant', variant.value)
		url.searchParams.set('section_id', 'waitlist')
		openBackInStockModal({
			opener,
			variantId: variant.value,
			klaviyoUrl: url.toString(),
		})
	}

	private get availabilityLabel() {
		if (this.dataset.ucCurrentInventoryStatus === 'ComingSoon')
			return this.dataset.ucComingSoonLabel ?? ''
		if (this.needsWaitlist)
			return `${this.dataset.ucSoldOutLabel ?? ''} · ${this.dataset.ucNotifyLabel ?? ''}`
		return this.dataset.ucPreorderLabel ?? ''
	}

	private hasDesign(design: ShoeDesign) {
		return design.slots.some(Boolean)
	}

	private hasSupportedDesign(design: ShoeDesign) {
		return (
			this.hasDesign(design) &&
			design.slots.every(
				(slot) =>
					!slot ||
					slot.kind === 'icon' ||
					supportsEmbroideryCharacter(design.font, slot.value)
			)
		)
	}

	private getShoe(shoe: Shoe) {
		if (shoe === 'right') return this.state.right
		if (!this.state.left) this.state.left = emptyShoe()
		return this.state.left
	}

	private getInputShoe(element: HTMLElement): Shoe {
		return element.dataset.ucPersonalizationShoe === 'left'
			? 'left'
			: 'right'
	}

	private getInputIndex(input: HTMLInputElement) {
		return Number(input.dataset.ucPersonalizationSlotIndex ?? 0)
	}

	private getSlotInput(shoe: Shoe, index: number) {
		return q.os<HTMLInputElement>(
			`[data-uc-personalization-slot-input][data-uc-personalization-shoe="${shoe}"][data-uc-personalization-slot-index="${index}"]`,
			this
		)
	}

	private render() {
		this.updateBackLink()
		this.renderStages()
		this.renderComposer('right')
		this.renderComposer('left')
		this.renderPreview()
		this.renderReview()
		const start = q.os<HTMLButtonElement>(
			'[data-uc-personalization-next="right"]',
			this
		)
		if (start) {
			start.disabled = !this.selectedVariant || this.submitting
			const label = q.os<HTMLElement>(
				'[data-uc-personalization-start-label]',
				start
			)
			if (label)
				label.textContent = this.needsWaitlist
					? this.availabilityLabel
					: (this.dataset.ucStartLabel ?? '')
		}

		const rightContinue = q.os<HTMLButtonElement>(
			'[data-uc-personalization-right-continue]',
			this
		)
		if (rightContinue)
			rightContinue.disabled = !this.hasSupportedDesign(this.state.right)
		const rightContinueLabel = q.os<HTMLElement>(
			'[data-uc-personalization-right-continue-label]',
			this
		)
		if (rightContinueLabel)
			rightContinueLabel.textContent =
				this.scope === 'both'
					? (this.dataset.ucPersonalizationDesignLeftLabel ?? '')
					: (this.dataset.ucPersonalizationContinueLabel ?? '')

		const leftComposer = q.os<HTMLElement>(
			'[data-uc-personalization-left-composer]',
			this
		)
		const leftContinue = q.os<HTMLButtonElement>(
			'[data-uc-personalization-left-continue]',
			this
		)
		const hasLeft = Boolean(this.state.left)
		if (leftComposer) leftComposer.hidden = !hasLeft
		if (leftContinue) {
			leftContinue.hidden = !hasLeft
			leftContinue.disabled =
				!this.state.left || !this.hasSupportedDesign(this.state.left)
		}

		for (const scope of this.querySelectorAll<HTMLInputElement>(
			'[data-uc-personalization-scope]'
		)) {
			scope.checked = scope.value === this.scope
		}

		const submit = q.os<HTMLButtonElement>(
			'[data-uc-personalization-submit]',
			this
		)
		if (submit) {
			submit.disabled = this.needsWaitlist
				? !this.selectedVariant || this.submitting
				: !this.isValid()
			const label = q.os<HTMLElement>(
				'[data-uc-personalization-submit-label]',
				submit
			)
			if (label)
				label.textContent = this.editLine
					? ((this.scope === 'none'
							? this.dataset.ucSavePlainLabel
							: this.dataset.ucSaveLabel) ?? '')
					: this.needsWaitlist ||
						  this.dataset.ucCurrentInventoryStatus === 'PreOrder'
						? this.availabilityLabel
						: this.addedBeat && this.addedCurrent
							? (this.dataset.ucAddedLabel ?? '')
							: this.addedCurrent
								? (this.dataset.ucAddAnotherLabel ?? '')
								: ((this.scope === 'none'
										? this.dataset.ucPlainSubmitLabel
										: this.dataset.ucSubmitLabel) ?? '')
		}
		this.persistDraft()
	}

	private renderStages() {
		for (const stage of this.querySelectorAll<HTMLElement>(
			'[data-uc-personalization-stage]'
		)) {
			const active = stage.dataset.ucPersonalizationStage === this.stage
			stage.hidden = !active
			stage.classList.toggle('is-active', active)
		}

		const steps = Array.from(
			this.querySelectorAll<HTMLButtonElement>(
				'.personalization-page__progress [data-uc-personalization-stage-target]'
			)
		)
		for (const step of steps) {
			const stage = step.dataset.ucPersonalizationStageTarget as Stage
			const active =
				stage === this.stage ||
				(stage === 'right' && this.stage === 'left')
			const unavailable = stage === 'review' && !this.isComplete
			step.disabled = unavailable
			step.classList.toggle('is-active', active)
			if (
				stage === 'right' &&
				this.scope === 'both' &&
				(this.stage === 'right' || this.stage === 'left')
			)
				step.dataset.ucPersonalizationDesignShoe = this.stage
			else delete step.dataset.ucPersonalizationDesignShoe
			if (active) step.setAttribute('aria-current', 'step')
			else step.removeAttribute('aria-current')
		}
	}

	private renderComposer(shoe: Shoe) {
		const design = shoe === 'right' ? this.state.right : this.state.left
		const composer = q.os<HTMLElement>(
			`[data-uc-personalization-composer="${shoe}"]`,
			this
		)
		if (!composer || !design) return
		composer.dataset.ucFont = design.font

		for (const [index, slot] of design.slots.entries()) {
			const input = this.getSlotInput(shoe, index)
			if (!input) continue
			const slotElement = input.closest<HTMLElement>(
				'[data-uc-personalization-slot]'
			)
			const iconElement = q.os<HTMLElement>(
				'[data-uc-personalization-slot-icon]',
				slotElement ?? composer
			)
			const glyphElement = q.os<HTMLElement>(
				'[data-uc-personalization-slot-glyph]',
				slotElement ?? composer
			)
			const color = slot?.color || this.getShoeColor(shoe, design)
			slotElement?.style.setProperty('--slot-color', color)
			slotElement?.style.setProperty(
				'--slot-outline',
				slot?.originalColors
					? 'transparent'
					: embroideryInputOutline(
							color,
							this.dataset.ucProductColorHex ?? ''
						)
			)
			slotElement?.classList.toggle(
				'is-active',
				this.activeSlot[shoe] === index
			)
			slotElement?.classList.toggle('has-value', Boolean(slot))
			input.value = slot?.kind === 'character' ? slot.value : ''
			const character =
				slot?.kind === 'character' ? slot.value : input.placeholder
			const glyphUrl =
				slot?.kind === 'icon'
					? ''
					: embroideryGlyphUrl(
							design.font,
							character,
							this.dataset.ucPersonalizationFontAsset ?? ''
						)
			const unsupported =
				slot?.kind === 'character' &&
				!supportsEmbroideryCharacter(design.font, slot.value)
			input.setAttribute('aria-invalid', String(Boolean(unsupported)))
			const nativeGlyph =
				design.font === 'block' &&
				slot?.kind !== 'icon' &&
				Boolean(character)
			slotElement?.classList.toggle(
				'has-glyph',
				Boolean(glyphUrl) || nativeGlyph
			)
			if (glyphElement) {
				if (nativeGlyph)
					renderEmbroideryText(glyphElement, character, color)
				else if (glyphUrl) {
					delete glyphElement.dataset.previewText
					renderEmbroideryGlyph(
						glyphElement,
						glyphUrl,
						color,
						false,
						true
					)
				} else {
					delete glyphElement.dataset.previewText
					delete glyphElement.dataset.previewGlyph
					glyphElement.replaceChildren()
				}
			}
			slotElement?.style.setProperty(
				'--glyph-url',
				glyphUrl ? `url("${glyphUrl}")` : 'none'
			)
			if (iconElement) {
				iconElement.classList.toggle(
					'is-visible',
					slot?.kind === 'icon'
				)
				iconElement.classList.toggle(
					'uses-original-color',
					slot?.kind === 'icon' && slot.originalColors
				)
				iconElement.style.setProperty(
					'--icon-url',
					slot?.iconUrl ? `url("${slot.iconUrl}")` : 'none'
				)
				iconElement.style.backgroundImage = ''
				if (slot?.kind === 'icon' && slot.iconUrl)
					renderEmbroideryGlyph(
						iconElement,
						slot.iconUrl,
						color,
						slot.originalColors
					)
			}
		}
		const fontError = q.os<HTMLElement>(
			'[data-uc-personalization-font-error]',
			composer
		)
		if (fontError)
			fontError.hidden = design.slots.every(
				(slot) =>
					!slot ||
					slot.kind === 'icon' ||
					supportsEmbroideryCharacter(design.font, slot.value)
			)

		for (const font of composer.querySelectorAll<HTMLInputElement>(
			'[data-uc-personalization-font]'
		)) {
			font.checked = font.value === design.font
		}

		const active = design.slots[this.activeSlot[shoe]]
		for (const icon of q.ol<HTMLButtonElement>(
			'[data-uc-personalization-icon]',
			composer
		) ?? []) {
			const selected =
				active?.kind === 'icon' &&
				active.value === icon.dataset.ucPersonalizationIconHandle
			icon.classList.toggle('is-selected', selected)
			icon.setAttribute('aria-pressed', String(selected))
		}
		const activeColor = this.getShoeColor(shoe, design)
		for (const color of composer.querySelectorAll<HTMLButtonElement>(
			'[data-uc-personalization-thread-color]'
		)) {
			const selected =
				color.dataset.ucPersonalizationColorHex === activeColor
			color.classList.toggle('is-selected', selected)
			color.setAttribute('aria-pressed', String(selected))
		}
		const selectedColor = composer.querySelector<HTMLButtonElement>(
			'[data-uc-personalization-thread-color].is-selected'
		)
		const colorName = q.os<HTMLElement>(
			'[data-uc-personalization-active-color-name]',
			composer
		)
		if (colorName)
			colorName.textContent =
				selectedColor?.dataset.ucPersonalizationColorName ?? ''
	}

	private renderPreview() {
		const preview = q.os<HTMLElement>('[data-uc-embroidery-preview]', this)
		if (preview) {
			preview.dataset.ucDesignVisible = String(
				this.stage !== 'pair' && this.scope !== 'none'
			)
			preview.dataset.ucZoom = String(
				this.stage === 'right' || this.stage === 'left'
			)
		}
		const rightPreview = q.os<HTMLElement>(
			'[data-uc-personalization-preview-shoe="right"]',
			this
		)
		if (rightPreview)
			rightPreview.hidden =
				this.activeShoe === 'left' && Boolean(this.state.left)
		this.renderPreviewShoe('right', this.state.right)
		const leftPreview = q.os<HTMLElement>(
			'[data-uc-personalization-preview-shoe="left"]',
			this
		)
		if (leftPreview)
			leftPreview.hidden = !this.state.left || this.activeShoe !== 'left'
		if (this.state.left) this.renderPreviewShoe('left', this.state.left)
		updateEmbroideryPreview(
			this,
			this.activeShoe,
			this.stage !== 'pair' && Boolean(this.state.left)
		)

		const label = q.os<HTMLElement>(
			'[data-uc-personalization-preview-label]',
			this
		)
		if (label)
			label.textContent =
				this.activeShoe === 'left'
					? (this.dataset.ucPersonalizationLeftLabel ?? '')
					: (this.dataset.ucPersonalizationRightLabel ?? '')
		const summary = q.os<HTMLElement>(
			'[data-uc-personalization-preview-summary]',
			this
		)
		if (summary)
			summary.textContent =
				this.scope === 'none'
					? ''
					: [
							this.shoeSummary(this.state.right),
							this.state.left
								? this.shoeSummary(this.state.left)
								: '',
						]
							.filter(Boolean)
							.join('. ')
	}

	private renderPreviewShoe(shoe: Shoe, design: ShoeDesign) {
		const preview = q.os<HTMLElement>(
			`[data-uc-personalization-preview-shoe="${shoe}"]`,
			this
		)
		if (!preview) return
		const previewRoot = q.os<HTMLElement>(
			'[data-uc-embroidery-preview]',
			this
		)
		const placeholderColor =
			previewRoot?.dataset.ucPlaceholderColor ||
			this.getShoeColor(shoe, design)
		const signature = JSON.stringify([
			design,
			this.dataset.ucPersonalizationPlaceholder,
			this.getShoeColor(shoe, design),
			placeholderColor,
			this.dataset.ucPersonalizationFontAsset,
		])
		if (preview.dataset.ucPersonalizationDesignSignature === signature)
			return
		preview.dataset.ucPersonalizationDesignSignature = signature
		preview.innerHTML = ''
		preview.dataset.ucFont = design.font
		const placeholder = this.dataset.ucPersonalizationPlaceholder ?? ''

		for (const [index, slot] of design.slots.entries()) {
			if (!slot && this.hasDesign(design)) continue
			const token = document.createElement('span')
			token.className = 'personalization-page__preview-token'
			if (!slot) {
				const placeholderCharacter =
					Array.from(placeholder)[index] ?? ''
				if (!placeholderCharacter) continue
				token.classList.add('is-placeholder')
				this.renderPreviewCharacter(
					token,
					design.font,
					placeholderCharacter,
					placeholderColor
				)
				token.style.color = placeholderColor
				preview.appendChild(token)
				continue
			}
			token.style.color = slot.color
			token.style.setProperty(
				'--preview-outline',
				slot.originalColors
					? 'transparent'
					: embroideryInputOutline(
							slot.color,
							this.dataset.ucProductColorHex ?? ''
						)
			)
			if (slot.kind === 'character')
				this.renderPreviewCharacter(
					token,
					design.font,
					slot.value,
					slot.color
				)
			else if (slot.iconUrl) {
				renderEmbroideryGlyph(
					token,
					slot.iconUrl,
					slot.color,
					slot.originalColors
				)
			}
			preview.appendChild(token)
		}
	}

	private renderPreviewCharacter(
		token: HTMLElement,
		font: ShoeDesign['font'],
		value: string,
		color: string
	) {
		if (font === 'block') {
			renderEmbroideryText(token, value, color, previewCharacterHeight)
			return
		}
		const url = embroideryGlyphUrl(
			font,
			value,
			this.dataset.ucPersonalizationFontAsset ?? ''
		)
		if (!url) return
		renderEmbroideryGlyph(
			token,
			url,
			color,
			false,
			previewCharacterHeight,
			true
		)
	}

	private renderReview() {
		const plain = this.scope === 'none'
		for (const element of this.querySelectorAll<HTMLElement>(
			'[data-uc-personalization-embroidery-only]'
		))
			element.hidden = plain
		const finalSale = q.os<HTMLElement>(
			'.personalization-page__final-sale',
			this
		)
		if (finalSale) finalSale.hidden = plain || Boolean(this.editLine)
		const removeRight = q.os<HTMLButtonElement>(
			'[data-uc-personalization-remove-right]',
			this
		)
		if (removeRight) removeRight.hidden = this.scope !== 'right'
		const upsellLabel = q.os<HTMLElement>(
			'[data-uc-personalization-upsell-label]',
			this
		)
		if (upsellLabel)
			upsellLabel.textContent = plain
				? (this.dataset.ucPersonalizationAddRightLabel ?? '')
				: (this.dataset.ucPersonalizationAddLeftLabel ?? '')
		const upsellPrice = q.os<HTMLElement>(
			'[data-uc-personalization-upsell-price]',
			this
		)
		if (upsellPrice)
			upsellPrice.textContent = `+${
				plain
					? (this.dataset.ucPersonalizationOnePriceFormatted ?? '')
					: (this.dataset.ucPersonalizationUpgradePriceFormatted ??
						'')
			}`
		const variant = this.selectedVariant
		const pair = q.os<HTMLElement>(
			'[data-uc-personalization-review-pair]',
			this
		)
		const right = q.os<HTMLElement>(
			'[data-uc-personalization-review-right]',
			this
		)
		const left = q.os<HTMLElement>(
			'[data-uc-personalization-review-left]',
			this
		)
		const leftRow = q.os<HTMLElement>(
			'[data-uc-personalization-review-left-row]',
			this
		)
		const upsell = q.os<HTMLElement>(
			'[data-uc-personalization-review-upsell]',
			this
		)
		const total = q.os<HTMLElement>(
			'[data-uc-personalization-review-total]',
			this
		)
		const slippersPrice = q.os<HTMLElement>(
			'[data-uc-personalization-review-slippers-price]',
			this
		)
		const embroideryPrice = q.os<HTMLElement>(
			'[data-uc-personalization-review-embroidery-price]',
			this
		)
		const gender = q.os<HTMLInputElement>(
			'[name="personalization-page-size-gender"]:checked',
			this
		)?.value
		if (pair)
			pair.textContent =
				(gender === 'men'
					? variant?.dataset.ucPersonalizationVariantMen
					: variant?.dataset.ucPersonalizationVariantWomen) ||
				variant?.dataset.ucPersonalizationVariantTitle ||
				''
		if (right) this.renderReviewShoe(right, 'right', this.state.right)
		if (left) this.renderReviewShoe(left, 'left', this.state.left)
		if (leftRow) leftRow.hidden = this.scope !== 'both'
		if (upsell) upsell.hidden = this.scope === 'both'
		const totalText =
			this.scope === 'both'
				? (variant?.dataset.ucPersonalizationTotalBoth ?? '')
				: (variant?.dataset.ucPersonalizationTotalOne ?? '')
		if (total) total.textContent = totalText
		if (slippersPrice)
			slippersPrice.textContent =
				variant?.dataset.ucPersonalizationPriceFormatted ?? ''
		if (embroideryPrice)
			embroideryPrice.textContent =
				this.scope === 'both'
					? (this.dataset.ucPersonalizationBothPriceFormatted ?? '')
					: (this.dataset.ucPersonalizationOnePriceFormatted ?? '')
	}

	private shoeSummary(design: ShoeDesign) {
		const values = design.slots.map((slot) => slot?.value).filter(Boolean)
		return values.length
			? `${values.join(' · ')} · ${design.font}`
			: (this.dataset.ucPersonalizationEmptyLabel ?? '')
	}

	private renderReviewShoe(
		element: HTMLElement,
		shoe: Shoe,
		design: ShoeDesign | null
	) {
		element.replaceChildren()
		if (!design) return
		const values = design.slots
			.filter((slot): slot is Slot => slot !== null)
			.map((slot) =>
				slot.kind === 'icon'
					? slot.value.replaceAll('-', ' ')
					: slot.value
			)
		const hasThreadColor = design.slots.some(
			(slot) => slot && !slot.originalColors
		)
		const fontInput = q.os<HTMLInputElement>(
			`[data-uc-personalization-font][data-uc-personalization-shoe="${shoe}"][value="${design.font}"]`,
			this
		)
		const fontLabel = fontInput?.parentElement
			? q
					.os<HTMLElement>(
						'[data-uc-personalization-font-name]',
						fontInput.parentElement
					)
					?.textContent?.trim()
			: ''
		const rows = [
			[
				this.dataset.ucPersonalizationDesignLabel,
				values.join(' · ') || this.dataset.ucPersonalizationEmptyLabel,
			],
			[this.dataset.ucPersonalizationFontLabel, fontLabel || design.font],
		]
		for (const [label, value] of rows) {
			const term = document.createElement('dt')
			term.textContent = label ?? ''
			const detail = document.createElement('dd')
			detail.textContent = value ?? ''
			element.append(term, detail)
		}
		const term = document.createElement('dt')
		term.textContent = this.dataset.ucPersonalizationThreadLabel ?? ''
		const detail = document.createElement('dd')
		if (hasThreadColor) {
			const colors = new Set(
				design.slots
					.filter((slot) => slot && !slot.originalColors)
					.map((slot) => slot!.color)
			)
			detail.textContent = [...colors]
				.map((color) => this.getColorName(color))
				.join(' · ')
		} else
			detail.textContent =
				this.dataset.ucPersonalizationOriginalColorsLabel ?? ''
		element.append(term, detail)
	}
	private buildProperties(groupId: string, parentId?: number) {
		if (this.scope === 'none') return {}
		const properties: Record<string, string> = {
			_personalization_version: '2',
			_personalization_state: JSON.stringify(this.state),
			_customization_group_id: groupId,
			'RIGHT SHOE': this.publicShoeSummary(this.state.right),
			'RIGHT FONT': this.state.right.font.toUpperCase(),
			'RIGHT COLOR': this.getColorName(
				this.getShoeColor('right', this.state.right)
			),
		}
		if (parentId) properties._parent_id = String(parentId)
		if (this.state.left) {
			properties['LEFT SHOE'] = this.publicShoeSummary(this.state.left)
			properties['LEFT FONT'] = this.state.left.font.toUpperCase()
			properties['LEFT COLOR'] = this.getColorName(
				this.getShoeColor('left', this.state.left)
			)
		}
		if (this.dataset.ucShippingCopy)
			properties['EMBROIDERY SHIPPING'] = this.dataset.ucShippingCopy
		return properties
	}

	private publicShoeSummary(design: ShoeDesign) {
		return design.slots
			.filter((slot): slot is Slot => slot !== null)
			.map((slot) =>
				slot.kind === 'icon'
					? slot.value.replaceAll('-', ' ')
					: slot.value
			)
			.join(' | ')
	}

	private getPersonalizationPrice() {
		if (this.scope === 'none') return 0
		return this.getDatasetNumber(
			this.state.left
				? this.dataset.ucPersonalizationBothPrice
				: this.dataset.ucPersonalizationOnePrice
		)
	}

	private getDatasetNumber(value: string | number | undefined) {
		const number = Number(value)
		return Number.isFinite(number) ? number : 0
	}

	private buildParentProperties(
		groupId: string,
		childId: number,
		existingProperties: Record<string, string> = {}
	) {
		if (this.scope === 'none')
			return this.plainProperties(existingProperties)
		const properties: Record<string, string> = {
			...existingProperties,
			_customization_group_id: groupId,
			_personalization_version: '2',
			_personalization_state: JSON.stringify(this.state),
			_personalization_variant_id: String(childId),
			_personalization_price: String(this.getPersonalizationPrice()),
			_personalization_right_shoe: this.publicShoeSummary(
				this.state.right
			),
			_personalization_right_font: this.state.right.font.toUpperCase(),
			_personalization_right_color: this.getColorName(
				this.getShoeColor('right', this.state.right)
			),
		}
		delete properties._plain_pair_id
		if (this.state.left) {
			properties._personalization_left_shoe = this.publicShoeSummary(
				this.state.left
			)
			properties._personalization_left_font =
				this.state.left.font.toUpperCase()
			properties._personalization_left_color = this.getColorName(
				this.getShoeColor('left', this.state.left)
			)
		} else {
			delete properties._personalization_left_shoe
			delete properties._personalization_left_font
			delete properties._personalization_left_color
		}
		if (this.dataset.ucShippingCopy) {
			properties._personalization_shipping = this.dataset.ucShippingCopy
		} else {
			delete properties._personalization_shipping
		}
		return properties
	}

	private async onSubmit(event: SubmitEvent) {
		event.preventDefault()
		this.syncVariantAvailability()
		const submit = q.os<HTMLButtonElement>(
			'[data-uc-personalization-submit]',
			this
		)
		if (!submit) return
		if (this.needsWaitlist) {
			this.openWaitlist(submit)
			return
		}
		if (!this.isValid()) return
		this.submitting = true
		submit.disabled = true
		submit.setAttribute('aria-disabled', 'true')
		submit.setAttribute('aria-busy', 'true')
		submit.setAttribute('data-uc-loading', '')
		const spinner = q.os<HTMLElement>(
			'[data-uc-loading-overlay-spinner]',
			submit
		)
		spinner?.classList.remove('hidden')
		this.hideError()

		try {
			if (this.editLine) await this.saveEdit()
			else {
				await this.addPersonalizedProduct()
				this.markAdded()
			}
			this.drafts = {}
			this.clearDraft()
		} catch (error) {
			this.showError(
				error instanceof Error
					? error.message
					: (this.dataset.ucAddError ?? '')
			)
		} finally {
			this.submitting = false
			submit.removeAttribute('aria-disabled')
			submit.removeAttribute('aria-busy')
			submit.removeAttribute('data-uc-loading')
			spinner?.classList.add('hidden')
			this.render()
		}
	}

	private async addPersonalizedProduct() {
		const parentId = this.getDatasetNumber(this.dataset.ucParentVariantId)
		const childId = this.getDatasetNumber(
			this.state.left
				? this.dataset.ucPersonalizationBothVariantId
				: this.dataset.ucPersonalizationOneVariantId
		)
		if (!parentId || (this.scope !== 'none' && !childId))
			throw new Error(this.dataset.ucAddError ?? '')
		const groupId = `customization-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`
		const parentProperties = this.buildParentProperties(groupId, childId)
		const parentItem: {
			id: number
			quantity: number
			properties: Record<string, string>
			selling_plan?: number
		} = { id: parentId, quantity: 1, properties: parentProperties }
		const sellingPlanId = Number(this.dataset.ucCurrentSellingPlanId)
		if (
			this.dataset.ucCurrentInventoryStatus === 'PreOrder' &&
			sellingPlanId
		) {
			parentItem.selling_plan = sellingPlanId
			parentProperties._preorder = 'true'
			if (this.dataset.ucCurrentShippingText) {
				parentProperties.PREORDER = this.dataset.ucCurrentShippingText
				parentProperties._stoq_shipping_text =
					this.dataset.ucCurrentShippingText
			}
		}

		const items: (typeof parentItem & { parent_id?: number })[] = [
			parentItem,
		]
		if (this.scope !== 'none')
			items.push({
				id: childId,
				quantity: 1,
				parent_id: parentId,
				properties: this.buildProperties(groupId, parentId),
			})
		const packagingId = Number(this.dataset.ucAdditionalPackagingVariantId)
		if (packagingId) {
			items.push({
				id: packagingId,
				quantity: 1,
				properties: { _additional_packaging: 'true' },
			})
		}

		const response = await addItemsToCart(items, getDOMCartSectionApiIds())
		if ('status' in response) throw new Error(response.description)
		publish(PUB_SUB_EVENTS.cartUpdate, {
			source: 'product-form',
			productVariantId: parentId,
		})
		renderResponseToCartDrawer(response)
	}

	private async initializeEditMode() {
		try {
			await this.refreshEditLines(this.editLine)
		} catch (error) {
			this.showError(
				error instanceof Error
					? error.message
					: (this.dataset.ucEditExpiredError ?? '')
			)
			return
		}
		if (!this.editParent) return
		const child = this.editChild
		const parent = this.editParent

		this.dataset.ucParentVariantId = String(parent.variant_id)
		this.dataset.ucCurrentInventoryStatus = 'InStock'
		this.classList.add('is-editing')
		this.querySelectorAll<HTMLInputElement>(
			'[name="personalization-parent-variant"]'
		).forEach((input) => {
			input.disabled = true
			input.checked = Number(input.value) === parent.variant_id
		})
		this.querySelectorAll<HTMLButtonElement>(
			'[data-uc-personalization-product-url]'
		).forEach((button) => {
			button.disabled = true
		})
		const finalSale = q.os<HTMLElement>(
			'.personalization-page__final-sale',
			this
		)
		if (finalSale) finalSale.hidden = true
		const label = q.os('[data-uc-personalization-submit-label]', this)
		if (label) label.textContent = this.dataset.ucSaveLabel ?? ''

		if (!child) {
			this.scope = 'none'
			this.stage = 'review'
			this.render()
			return
		}

		const serializedState = child.properties._personalization_state
		if (serializedState) {
			try {
				this.hydrateState(
					JSON.parse(serializedState) as PersonalizationState
				)
			} catch {
				this.hydrateLegacyProperties(child.properties)
			}
		} else {
			this.hydrateLegacyProperties(child.properties)
		}
		this.scope = this.state.left ? 'both' : 'right'
		this.stage = 'review'
		this.lastDesignStage = this.state.left ? 'left' : 'right'
		this.render()
	}

	private hydrateState(state: PersonalizationState) {
		if (state.version !== 2 || !state.right) return
		if (!this.hasValidStateSlots(state))
			throw new TypeError('Invalid personalization state')
		this.state = state
		this.enrichIconSlots()
		for (const shoe of ['right', 'left'] as const) {
			const design = shoe === 'right' ? state.right : state.left
			if (!design) continue
			this.normalizeShoeColors(shoe, design)
		}
	}

	private hydrateLegacyProperties(properties: Record<string, string>) {
		const hydrate = (value: string | undefined): ShoeDesign | null => {
			if (!value) return null
			const design = emptyShoe()
			for (const [index, rawToken] of value
				.split('|')
				.slice(0, 3)
				.entries()) {
				const match = rawToken
					.trim()
					.match(/^(.+?)\s*\((#[0-9a-f]{3,8})\)$/i)
				const token = match?.[1]?.trim() ?? rawToken.trim()
				const color = match?.[2] ?? this.pendingColors.right
				design.slots[index] = {
					kind: token.length === 1 ? 'character' : 'icon',
					value: token,
					color,
				}
			}
			return design
		}
		this.state.right =
			hydrate(properties['RIGHT SHOE'] ?? properties['Right Shoe']) ??
			emptyShoe()
		this.state.left = hydrate(
			properties['LEFT SHOE'] ?? properties['Left Shoe']
		)
		const rightFont = (
			properties['RIGHT FONT'] ?? properties['Right Font']
		)?.toLowerCase()
		const leftFont = (
			properties['LEFT FONT'] ?? properties['Left Font']
		)?.toLowerCase()
		if (['bubble', 'script', 'block'].includes(rightFont))
			this.state.right.font = rightFont as ShoeDesign['font']
		if (this.state.left && ['bubble', 'script', 'block'].includes(leftFont))
			this.state.left.font = leftFont as ShoeDesign['font']
		this.enrichIconSlots()
		this.normalizeShoeColors('right', this.state.right)
		if (this.state.left) this.normalizeShoeColors('left', this.state.left)
	}

	private enrichIconSlots() {
		const definitions = this.iconDefinitions
		for (const design of [this.state.right, this.state.left]) {
			if (!design) continue
			for (const slot of design.slots) {
				if (slot?.kind !== 'icon') continue
				const normalizedValue = slot.value.toLowerCase()
				const definition =
					definitions.get(slot.value) ??
					Array.from(definitions.values()).find(
						(icon) =>
							icon.handle.replaceAll('-', ' ').toLowerCase() ===
							normalizedValue
					)
				if (!definition) continue
				slot.value = definition.handle
				slot.iconUrl = definition.url
				slot.originalColors = definition.useOriginalColors
			}
		}
	}

	private async refreshEditLines(reference: string) {
		const [cart, components] = await Promise.all([
			getCart(),
			getPersonalizationCartComponents(),
		])
		if (!('items' in cart)) throw new Error(cart.description)
		const lines = cart.items as CartLine[]
		const component = components.find(
			(item) => item.groupId === reference || item.childKey === reference
		)
		const groupId = component?.groupId || reference
		const parent = lines.find(
			(line) =>
				line.key === component?.parentKey ||
				line.properties?._customization_group_id === groupId ||
				line.properties?._plain_pair_id === groupId
		)
		if (component?.childKey && component.childVariantId && parent) {
			this.editChild = {
				id: component.childVariantId,
				variant_id: component.childVariantId,
				product_id: this.getDatasetNumber(
					this.dataset.ucPersonalizationProductId
				),
				key: component.childKey,
				quantity: 1,
				properties: {
					...component.properties,
					_customization_group_id: component.groupId,
					_personalization_state:
						component.state ||
						parent.properties._personalization_state ||
						'',
				},
			}
			this.editParent = parent
			return
		}
		if (parent) {
			this.editParent = parent
			this.editChild = undefined
			return
		}
		const variantId = this.getDatasetNumber(this.dataset.ucParentVariantId)
		const productId = this.getDatasetNumber(this.dataset.ucParentProductId)
		const personalizedParents = new Set(
			components.map((item) => item.parentKey)
		)
		const orphans = lines.filter(
			(line) =>
				line.variant_id === variantId &&
				(!productId || line.product_id === productId) &&
				!personalizedParents.has(line.key)
		)
		if (orphans.length !== 1)
			throw new Error(this.dataset.ucEditExpiredError)
		this.editParent = orphans[0]
		this.editChild = undefined
	}

	private plainProperties(existing: Record<string, string> = {}) {
		return Object.fromEntries(
			Object.entries(existing).filter(
				([key]) =>
					!key.startsWith('_personalization_') &&
					key !== '_customization_group_id'
			)
		)
	}

	private async changeCartLine(
		line: CartLine,
		properties: Record<string, string>,
		sections = false
	) {
		const response = await fetch(
			window.routes.cart_change_url,
			configPostRequest({
				id: line.key,
				quantity: line.quantity,
				properties,
				...(sections
					? {
							sections: getDOMCartSectionApiIds(),
							sections_url: window.location.pathname,
						}
					: {}),
			})
		).then((result) => result.json())
		if (response.status) throw new Error(response.description)
		return response
	}

	private async removeEditedEmbroidery() {
		const groupId = this.editChild?.properties._customization_group_id
		if (!groupId) throw new Error(this.dataset.ucEditExpiredError)
		await this.refreshEditLines(groupId)
		const parent = this.editParent!
		const child = this.editChild!
		let removeResponse
		try {
			removeResponse = await fetch(
				window.routes.cart_change_url,
				configPostRequest({ id: child.key, quantity: 0 })
			).then((result) => result.json())
			if (removeResponse.status)
				throw new Error(removeResponse.description)
		} catch (error) {
			const components = await getPersonalizationCartComponents()
			if (
				components.some(
					(component) =>
						component.groupId === groupId && component.childKey
				)
			)
				throw error
			removeResponse = await getCart()
			if (!('items' in removeResponse)) throw error
		}
		const remainingParent = (removeResponse.items as CartLine[]).find(
			(line) =>
				line.properties?._customization_group_id === groupId &&
				line.product_id === parent.product_id
		)
		if (!remainingParent)
			throw new Error(this.dataset.ucProductNotFoundError)
		// Shopify cannot clear all properties with {}; a private, non-embroidery ID also keeps this line identifiable.
		const properties = {
			...this.plainProperties(parent.properties),
			_plain_pair_id: groupId,
		}
		let response
		try {
			response = await this.changeCartLine(
				remainingParent,
				properties,
				true
			)
		} catch (error) {
			const cart = await getCart()
			if (!('items' in cart)) throw error
			const committed = (cart.items as CartLine[]).find(
				(line) => line.properties?._plain_pair_id === groupId
			)
			if (committed)
				response = await this.changeCartLine(
					committed,
					properties,
					true
				)
			else {
				await this.restorePreviousPersonalization(
					groupId,
					child,
					parent.properties
				)
				await this.refreshEditLines(groupId)
				throw error
			}
		}
		const plainParent = (response.items as CartLine[]).find(
			(line) => line.properties?._plain_pair_id === groupId
		)
		if (!plainParent) throw new Error(this.dataset.ucProductNotFoundError)
		this.editParent = plainParent
		this.editChild = undefined
		renderResponseToCartDrawer(response)
	}

	private async savePlainParentEdit() {
		const plainId = this.editParent?.properties._plain_pair_id
		const cart = await getCart()
		if (!('items' in cart)) throw new Error(cart.description)
		const parent = (cart.items as CartLine[]).find(
			(line) => plainId && line.properties?._plain_pair_id === plainId
		)
		if (!parent) throw new Error(this.dataset.ucEditExpiredError)
		this.editParent = parent
		if (this.scope === 'none') {
			renderResponseToCartDrawer(
				await this.changeCartLine(parent, parent.properties, true)
			)
			return
		}
		const childId = this.getDatasetNumber(
			this.scope === 'both'
				? this.dataset.ucPersonalizationBothVariantId
				: this.dataset.ucPersonalizationOneVariantId
		)
		if (!childId || !plainId) throw new Error(this.dataset.ucAddError)
		try {
			const parentResponse = await this.changeCartLine(
				parent,
				this.buildParentProperties(plainId, childId, parent.properties)
			)
			const updatedParent = (parentResponse.items as CartLine[]).find(
				(line) =>
					line.properties?._customization_group_id === plainId &&
					line.product_id === parent.product_id
			)
			if (!updatedParent)
				throw new Error(this.dataset.ucProductNotFoundError)
			const response = await addItemsToCart(
				[
					{
						id: childId,
						quantity: 1,
						parent_line_key: updatedParent.key,
						properties: this.buildProperties(
							plainId,
							parent.variant_id
						),
					},
				],
				getDOMCartSectionApiIds()
			)
			if ('status' in response) throw new Error(response.description)
			await this.refreshEditLines(plainId)
			renderResponseToCartDrawer(response)
		} catch (error) {
			const components = await getPersonalizationCartComponents()
			if (
				components.some(
					(component) =>
						component.groupId === plainId && component.childKey
				)
			) {
				await this.refreshEditLines(plainId)
			} else {
				const currentCart = await getCart()
				if (!('items' in currentCart)) throw error
				const currentParent = (currentCart.items as CartLine[]).find(
					(line) =>
						line.product_id === parent.product_id &&
						(line.properties?._customization_group_id === plainId ||
							line.properties?._plain_pair_id === plainId)
				)
				if (!currentParent) throw error
				const restored = await this.changeCartLine(
					currentParent,
					parent.properties
				)
				this.editParent = (restored.items as CartLine[]).find(
					(line) => line.properties?._plain_pair_id === plainId
				)
			}
			throw error
		}
	}

	private async saveEdit() {
		if (!this.editChild && this.editParent) {
			await this.savePlainParentEdit()
			return
		}
		if (!this.editChild || !this.editParent)
			throw new Error(this.dataset.ucEditExpiredError)
		if (this.scope === 'none') {
			await this.removeEditedEmbroidery()
			return
		}
		const groupId = this.editChild.properties._customization_group_id
		await this.refreshEditLines(groupId)
		if (!this.editChild || !this.editParent)
			throw new Error(this.dataset.ucEditExpiredError)
		const properties = this.buildProperties(
			groupId,
			this.editParent.variant_id
		)
		const expectedVariantId = this.getDatasetNumber(
			this.state.left
				? this.dataset.ucPersonalizationBothVariantId
				: this.dataset.ucPersonalizationOneVariantId
		)
		if (!expectedVariantId) throw new Error(this.dataset.ucAddError ?? '')
		const sections = getDOMCartSectionApiIds()
		const parentProperties = this.buildParentProperties(
			groupId,
			expectedVariantId,
			this.editParent.properties
		)

		if (this.editChild.variant_id === expectedVariantId) {
			const previousChildProperties = { ...this.editChild.properties }
			const previousChildQuantity = this.editChild.quantity
			const childResponse = await fetch(
				window.routes.cart_change_url,
				configPostRequest({
					id: this.editChild.key,
					quantity: 1,
					properties,
				})
			).then((result) => result.json())
			if (childResponse.status) throw new Error(childResponse.description)
			const updatedChild = (
				childResponse.items as CartLine[] | undefined
			)?.find(
				(line) =>
					line.variant_id === expectedVariantId &&
					line.properties?._customization_group_id === groupId
			)
			if (!updatedChild) throw new Error(this.dataset.ucEditExpiredError)
			this.editChild = updatedChild
			const parentResponse = await fetch(
				window.routes.cart_change_url,
				configPostRequest({
					id: this.editParent.key,
					quantity: this.editParent.quantity,
					properties: parentProperties,
					sections,
					sections_url: window.location.pathname,
				})
			).then((result) => result.json())
			if (parentResponse.status) {
				const rollbackResponse = await fetch(
					window.routes.cart_change_url,
					configPostRequest({
						id: this.editChild.key,
						quantity: previousChildQuantity,
						properties: previousChildProperties,
					})
				).then((result) => result.json())
				if (rollbackResponse.status)
					throw new Error(rollbackResponse.description)
				throw new Error(parentResponse.description)
			}
			await this.refreshEditLines(groupId)
			renderResponseToCartDrawer(parentResponse)
			return
		}

		const previousChild = { ...this.editChild }
		const previousParentProperties = { ...this.editParent.properties }
		const removeResponse = await fetch(
			window.routes.cart_change_url,
			configPostRequest({
				id: this.editChild.key,
				quantity: 0,
			})
		).then((result) => result.json())
		if (removeResponse.status) throw new Error(removeResponse.description)
		let addResponse
		try {
			const parentResponse = await fetch(
				window.routes.cart_change_url,
				configPostRequest({
					id: this.editParent.key,
					quantity: this.editParent.quantity,
					properties: parentProperties,
				})
			).then((result) => result.json())
			if (parentResponse.status)
				throw new Error(parentResponse.description)
			const cart = await getCart()
			if (!('items' in cart)) throw new Error(cart.description)
			const refreshedParent = (cart.items as CartLine[]).find(
				(line) =>
					line.properties?._customization_group_id === groupId &&
					line.product_id !==
						this.getDatasetNumber(
							this.dataset.ucPersonalizationProductId
						)
			)
			if (!refreshedParent)
				throw new Error(this.dataset.ucProductNotFoundError)
			addResponse = await addItemsToCart(
				[
					{
						id: expectedVariantId,
						quantity: 1,
						parent_line_key: refreshedParent.key,
						properties,
					},
				],
				sections
			)
			if ('status' in addResponse)
				throw new Error(addResponse.description)
		} catch (error) {
			try {
				await this.restorePreviousPersonalization(
					groupId,
					previousChild,
					previousParentProperties
				)
			} catch (restoreError) {
				console.error(restoreError)
				throw new Error(this.dataset.ucRestoreFailedError ?? '', {
					cause: error,
				})
			}
			throw error
		}
		await this.refreshEditLines(groupId)
		renderResponseToCartDrawer(addResponse)
	}

	private async restorePreviousPersonalization(
		groupId: string,
		child: CartLine,
		parentProperties: Record<string, string>
	) {
		const cart = await getCart()
		if (!('items' in cart)) throw new Error(cart.description)
		const parent = (cart.items as CartLine[]).find(
			(line) =>
				line.properties?._customization_group_id === groupId &&
				line.product_id !==
					this.getDatasetNumber(
						this.dataset.ucPersonalizationProductId
					)
		)
		if (!parent) throw new Error(this.dataset.ucProductNotFoundError)
		const parentResponse = await fetch(
			window.routes.cart_change_url,
			configPostRequest({
				id: parent.key,
				quantity: parent.quantity,
				properties: parentProperties,
			})
		).then((result) => result.json())
		if (parentResponse.status) throw new Error(parentResponse.description)
		const restoredCart = await getCart()
		if (!('items' in restoredCart))
			throw new Error(restoredCart.description)
		const restoredParent = (restoredCart.items as CartLine[]).find(
			(line) =>
				line.properties?._customization_group_id === groupId &&
				line.product_id !==
					this.getDatasetNumber(
						this.dataset.ucPersonalizationProductId
					)
		)
		if (!restoredParent)
			throw new Error(this.dataset.ucProductNotFoundError)
		const response = await addItemsToCart([
			{
				id: child.variant_id,
				quantity: child.quantity,
				parent_line_key: restoredParent.key,
				properties: child.properties,
			},
		])
		if ('status' in response) throw new Error(response.description)
	}

	private async changeProduct(productUrl: string) {
		if (!productUrl || this.editLine || this.changingProduct) return
		this.changingProduct = true
		try {
			for (const button of this.querySelectorAll<HTMLButtonElement>(
				'[data-uc-personalization-product-url]'
			))
				button.setAttribute('aria-disabled', 'true')
			const selectedVariant = this.selectedVariant
			const gender =
				q.os<HTMLInputElement>(
					'[name="personalization-page-size-gender"]:checked',
					this
				)?.value ?? ''
			const transfer: PersonalizationTransfer = {
				state: this.state,
				scope: this.scope,
				drafts: this.drafts,
				sizeLabel: selectedVariant
					? this.getVariantSizeLabel(selectedVariant, gender)
					: '',
				gender,
			}
			window.sessionStorage.setItem(
				this.transferKey,
				JSON.stringify(transfer)
			)
			const requestUrl = getUrlWithPreviewThemeId(productUrl)
			requestUrl.searchParams.set('view', 'personalize')
			this.preserveReturnTo(requestUrl)
			requestUrl.searchParams.set(
				'section_id',
				this.dataset.ucSectionId ?? ''
			)
			const response = await fetch(requestUrl.toString(), {
				credentials: 'same-origin',
			})
			if (!response.ok) throw new Error(this.dataset.ucColorLoadError)
			const html = await response.text()
			await this.preloadProductPreview(html)
			requestUrl.searchParams.delete('section_id')
			requestUrl.searchParams.set('step', 'pair')
			const product =
				requestUrl.pathname.split('/').filter(Boolean).at(-1) ?? ''
			window.history.replaceState(
				this.stageHistoryState('pair', this.stageHistoryDepth, product),
				'',
				requestUrl.toString()
			)
			this.outerHTML = html
		} catch (error) {
			try {
				window.sessionStorage.removeItem(this.transferKey)
			} catch {
				// Storage cleanup is best-effort; the visible error still needs to render.
			}
			this.showError(
				error instanceof Error
					? error.message
					: (this.dataset.ucColorLoadError ?? '')
			)
		} finally {
			this.changingProduct = false
			for (const button of this.querySelectorAll<HTMLButtonElement>(
				'[data-uc-personalization-product-url]'
			))
				button.removeAttribute('aria-disabled')
		}
	}

	private async preloadProductPreview(html: string) {
		const template = document.createElement('template')
		template.innerHTML = html
		const candidate = template.content.querySelector<HTMLImageElement>(
			'.personalization-page__preview-image'
		)
		if (!candidate) return
		const image = document.createElement('img')
		image.fetchPriority = 'high'
		image.sizes = candidate.sizes
		image.srcset = candidate.srcset
		image.src = candidate.src
		try {
			await image.decode()
		} catch {
			return
		}
	}

	private showError(message: string) {
		const error = q.os<HTMLElement>('[data-uc-personalization-error]', this)
		if (!error) return
		error.textContent = message
		error.hidden = false
	}

	private hideError() {
		const error = q.os<HTMLElement>('[data-uc-personalization-error]', this)
		if (error) error.hidden = true
	}
}
